import React, { useCallback, useMemo } from 'react';
import styles from './styles.module.scss';

import { NoDataTableRow, TableHeadComponent, TableWrapper } from 'components/SummaryComponents';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import StyledLink from 'components/StyledLink';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';

import { IconButton, Table, TableBody } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import { BILL_OF_LADING_DETAILS_PATH, LOCATION_DETAILED_PATH } from 'constants/routeConstants';

import { getChangedBySortFilter } from 'helpers/SummaryPagesHelpers';
import { getLocationWithSublocationAndAssetName } from 'helpers/AppHelpers';

import {
  NotesCellContent,
  PickupDateCellContent,
  TruckOrTrailerCellContent,
  StatusCellContent,
  FileCellContent
} from '../Cells';

export function PicklistTableBase({
  tableConfig,
  tableData = [],
  filter = {},
  permissions,
  assetStatuses,
  isTableFiltered = false,
  selectionState,
  getPicklistAssets,
  updatePicklistAsset,
  onDeleteSingleAsset,
  onDeleteFile,
  addDataLabel,
  renderNameCell
}) {
  const { sortRules = [] } = filter;

  const {
    isPicklistAssetSelectionAvailable,
    canEditNotes,
    canEditPickupDate,
    canEditTruckOrTrailer,
    canEditAssetStatus,
    canUploadFile,
    canRemoveAsset
  } = permissions;

  const { isAllSelected, toggleAllSelection, isRowSelected, toggleRowSelection } = selectionState;

  const createTableConfig = (isAllSelected, toggleAllSelection) => {
    const config = [...tableConfig];

    if (isPicklistAssetSelectionAvailable) {
      config.unshift({
        id: 'checkbox',
        label: '',
        renderContent: () => (
          <CustomCheckbox
            sendEvent
            isDisabled={tableData.length === 0}
            value={isAllSelected()}
            onChange={(e) => toggleAllSelection(e.target.checked)}
          />
        )
      });
    }

    config.push({
      id: 'action',
      label: ''
    });

    return config;
  };

  const tableHeadConfig = useMemo(
    () => createTableConfig(isAllSelected, toggleAllSelection),
    [tableConfig, isAllSelected, toggleAllSelection, isPicklistAssetSelectionAvailable]
  );

  const sortTable = useCallback(
    (rowName) => getPicklistAssets(getChangedBySortFilter(filter, rowName)),
    [getPicklistAssets]
  );

  const updateRow = useCallback(
    (row) => {
      updatePicklistAsset(row);
    },
    [updatePicklistAsset]
  );

  const handleDeletePicklistAsset = (id) => onDeleteSingleAsset(id);

  return (
    <TableWrapper enableHorizontalScroll>
      <Table>
        <TableHeadComponent
          config={tableHeadConfig}
          sortRules={sortRules}
          onSortApply={sortTable}
        />
        <TableBody>
          {tableData?.length ? (
            tableData.map((row, index) => (
              <StyledTableRow key={row.id} className={styles.picklist_asset_row}>
                {isPicklistAssetSelectionAvailable && (
                  <StyledTableCell className={styles.checkboxCell}>
                    <CustomCheckbox
                      sendEvent
                      value={isRowSelected(index)}
                      onChange={(e) => toggleRowSelection(index, e.target.checked)}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell className={styles.picklistAssetCell}>
                  {renderNameCell(row)}
                </StyledTableCell>
                <StyledTableCell className={styles.locationCell}>
                  <StyledLink
                    to={`${LOCATION_DETAILED_PATH}/${row.location?.id}`}
                    isRedirectAvailable={!row.location?.deleted && row?.location?.id}>
                    {getLocationWithSublocationAndAssetName(row)}
                  </StyledLink>
                </StyledTableCell>
                <StyledTableCell className={styles.notesCell}>
                  <NotesCellContent row={row} onEdit={updateRow} isEditable={canEditNotes} />
                </StyledTableCell>
                <StyledTableCell className={styles.pickupDateCell}>
                  <PickupDateCellContent
                    row={row}
                    onEdit={updateRow}
                    isEditable={canEditPickupDate}
                  />
                </StyledTableCell>
                <StyledTableCell className={styles.statusCell}>
                  <StatusCellContent
                    row={row}
                    onEdit={updateRow}
                    isEditable={canEditAssetStatus(row)}
                    statuses={assetStatuses}
                  />
                </StyledTableCell>
                <StyledTableCell className={styles.truckCell}>
                  <TruckOrTrailerCellContent
                    row={row}
                    onEdit={updateRow}
                    isEditable={canEditTruckOrTrailer}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <StyledLink
                    to={`${BILL_OF_LADING_DETAILS_PATH}/${row?.billOfLading?.id}`}
                    isRedirectAvailable={!!row?.billOfLading?.id}>
                    {row?.billOfLading?.freightBill || ''}
                  </StyledLink>
                </StyledTableCell>
                <StyledTableCell className={styles.fileCell}>
                  <FileCellContent
                    row={row}
                    onUploadFile={updatePicklistAsset}
                    onDeleteFile={onDeleteFile}
                    isEditable={canUploadFile}
                  />
                </StyledTableCell>
                <StyledTableCell className={styles.deleteCell}>
                  {canRemoveAsset(row) && (
                    <IconButton onClick={() => handleDeletePicklistAsset(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))
          ) : (
            <NoDataTableRow
              addDataLabel={addDataLabel}
              isTableFiltered={isTableFiltered}
            />
          )}
        </TableBody>
      </Table>
    </TableWrapper>
  );
}
